import React, { useCallback, useEffect, useState } from "react";
import { getAuth, signOut, User } from "firebase/auth";
import { Mail, CheckCircle2, AlertCircle } from "lucide-react";
import { app } from "../firebase";
import { requestVerificationEmail, wasVerificationSent } from "../utils/emailVerification";

interface VerifyEmailProps {
  user: User;
  /** Called once the address is confirmed, so the caller can show the portal. */
  onVerified: () => void;
}

const CHECK_EVERY_MS = 4000;

/**
 * Shown instead of the portal until the account's email address is confirmed. New accounts already have the email from signup;
 * older accounts are not emailed on their own, they choose to send it here.
 */
const VerifyEmail: React.FC<VerifyEmailProps> = ({ user, onVerified }) => {
  const [sent, setSent] = useState(() => wasVerificationSent(user.uid));
  const [sending, setSending] = useState(false);
  const [waitSeconds, setWaitSeconds] = useState(0);
  const [problem, setProblem] = useState("");

  const checkVerified = useCallback(async () => {
    try {
      await user.reload();
      if (user.emailVerified) {
        await user.getIdToken(true); // so the token's email_verified claim is current
        onVerified();
      }
    } catch (error) {
      console.error("Could not re-check the email confirmation:", error);
    }
  }, [user, onVerified]);

  // The link opens in another tab or on another device, so keep checking while this screen is up.
  useEffect(() => {
    const timer = setInterval(checkVerified, CHECK_EVERY_MS);
    window.addEventListener("focus", checkVerified);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", checkVerified);
    };
  }, [checkVerified]);

  useEffect(() => {
    if (waitSeconds <= 0) return;
    const timer = setTimeout(() => setWaitSeconds((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [waitSeconds]);

  const send = async () => {
    setSending(true);
    setProblem("");
    const result = await requestVerificationEmail(() => user.getIdToken());
    setSending(false);
    if (result.status === "sent") {
      setSent(true);
      setWaitSeconds(60);
    } else if (result.status === "wait") {
      setSent(true);
      setWaitSeconds(result.retryAfterSeconds);
    } else if (result.status === "already-verified") {
      await checkVerified();
    } else {
      setProblem("We couldn't send the email. Please try again in a moment.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-inter flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-sm border border-slate-100 w-full max-w-md p-8 text-center space-y-6">
        <div className="mx-auto w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
          <Mail size={28} />
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-slate-900">Confirm your email</h1>
          {sent ? (
            <p className="text-slate-500">
              We sent a confirmation link to <strong className="text-slate-700 break-all">{user.email}</strong>. Open it, then come back here. This page continues on its own.
            </p>
          ) : (
            <p className="text-slate-500">
              To keep your account secure, please confirm <strong className="text-slate-700 break-all">{user.email}</strong> before continuing.
            </p>
          )}
        </div>

        {sent && !problem && (
          <div className="p-3 rounded-2xl bg-green-50 text-green-700 border border-green-100 flex items-center justify-center gap-2 text-sm font-medium">
            <CheckCircle2 size={18} /> Check your inbox, and your spam folder
          </div>
        )}
        {problem && (
          <div role="alert" className="p-3 rounded-2xl bg-red-50 text-red-700 border border-red-100 flex items-center justify-center gap-2 text-sm font-medium">
            <AlertCircle size={18} /> {problem}
          </div>
        )}

        <div className="space-y-3">
          <button
            type="button"
            onClick={send}
            disabled={sending || waitSeconds > 0}
            className="btn-primary w-full disabled:opacity-60"
          >
            {sending ? "Sending..." : waitSeconds > 0 ? `Send again in ${waitSeconds}s` : sent ? "Send it again" : "Send confirmation email"}
          </button>
          <button type="button" onClick={checkVerified} className="btn-secondary w-full">
            I've confirmed it
          </button>
          <button
            type="button"
            onClick={() => signOut(getAuth(app))}
            className="text-sm text-slate-500 hover:text-slate-700 underline"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
